import { errorHandler } from 'utilities/errorHandler'
import { getErrorCode } from 'utilities/getErrorCode'
import { isPlainObject } from 'utilities/isPlainObject'
import { api } from './index'
import type {
  ActivateRecoveryBody,
  ActivationSummaryResponse,
  Activity,
  ActivityMetrics,
  ActivityMetricsResponse,
  ActivityQueryParams,
  ActivityResponse,
  AvailableReward,
  BrandVoiceDerivation,
  BudgetSummary,
  CannedResponse,
  CannedResponsesResponseWire,
  CannedResponseWire,
  Conversation,
  ConversationWire,
  Escalation,
  EscalationWire,
  FlaggedCustomer,
  GamingReviewCustomer,
  GamingReviewCustomerWire,
  GamingReviewQueryParams,
  LocationConfigRow,
  LocationConfigRowWire,
  LocationConfigsSummary,
  LocationConfigsSummaryWire,
  NotifyActivationAdminParams,
  OperatingMode,
  PaginatedResponse,
  PaginatedResponseWire,
  PaginationMeta,
  PaginationMetaWire,
  PreparerAdmin,
  PreparerAdminWire,
  RecoveryConfig,
  RecoveryStatus,
  ResolveCaseParams,
  ResolvedByKind,
  ResolveEscalationParams,
  RestrictCustomerParams,
  RestrictedCustomer,
  RestrictedCustomerQueryParams,
  RestrictedCustomerWire,
  SandboxPrompt,
  SendConversationMessageParams,
  UpdateLocationConfigsBody,
  UploadBrandVoiceDocumentBody,
  WhitelistParams,
} from './recoveryAi.types'
import { isFetchBaseQueryError } from './utils'

// Normalize the wire-shape pagination meta (snake_case) into the camelCase
// shape components read. Kept here so every paginated transformResponse can
// re-use it without re-implementing the mapping.
const normalizePaginationMeta = (wire: PaginationMetaWire): PaginationMeta => ({
  currentPage: wire.current_page,
  totalPages: wire.total_pages,
  totalCount: wire.total_count,
  perPage: wire.per_page,
})

// Maps a RestrictedCustomerEntity wire row (#569 + AI-178 #617) to the camelCase
// domain `RestrictedCustomer`. The wire already ships `id` and
// `context.conversation_id` as strings (`restriction.id.to_s` / `conversation&.id&.to_s`),
// so both pass through verbatim; `user_id` is the lone bare integer and is
// stringified at the boundary. `complaint_rate` is kept RAW (the uncapped ratio) —
// the table cell formats it to a whole percent, not this boundary. Nil-safe: a null
// restricted_by / context or a missing aggregate degrades to a stable default,
// never undefined, so consumers read a uniform row shape. Exported for a dedicated
// transform unit test (the slice's transformResponse delegates here).
export const transformRestrictedCustomer = (
  wire: RestrictedCustomerWire
): RestrictedCustomer => ({
  id: wire.id,
  userId: String(wire.user_id),
  customerName: wire.customer_name,
  restrictedAt: wire.restricted_at,
  status: wire.status,
  restrictedBy: {
    kind: wire.restricted_by?.kind ?? null,
    name: wire.restricted_by?.name ?? null,
  },
  conversationId: wire.context?.conversation_id ?? null,
  storeName: wire.context?.store_name ?? null,
  complaints30d: wire.complaints_30d ?? 0,
  visits30d: wire.visits_30d ?? 0,
  complaintRate: wire.complaint_rate ?? 0,
})

// AI-303 (FR-41 gaming-review): maps a suspected-gaming queue wire row to the
// camelCase domain shape. Mirrors transformRestrictedCustomer — `user_id` is the
// lone bare integer and is stringified; the per-row aggregates nil-safe to 0 and
// the nullable source fields to null, so the pane reads a uniform row and never
// crashes on a partial wire row. Exported for a dedicated transform unit test
// (the slice's transformResponse delegates here).
export const transformGamingReviewRow = (
  wire: GamingReviewCustomerWire
): GamingReviewCustomer => ({
  userId: String(wire.user_id),
  customerName: wire.customer_name,
  autoResolutionCount: wire.auto_resolution_count ?? 0,
  periodDays: wire.period_days ?? 0,
  lastAutoResolvedAt: wire.last_auto_resolved_at ?? null,
  // `|| null` (not `??`) so an empty-string conversation_id coalesces to null
  // too: a blank id is not a usable source conversation, and the downstream
  // Restrict guard is keyed on `conversationId !== null`. Without this, a wire
  // `conversation_id: ''` would leave the Restrict action enabled and fire a
  // doomed POST with an empty conversation_id (the backend rejects it).
  conversationId: wire.conversation_id || null,
  storeName: wire.store_name ?? null,
})

// Canned responses (AI-249): maps one wire entry to the camelCase domain shape.
// Nil-safe by construction — a fallback/malformed row coerces `body` to '' and
// `generated` to false so the pane reads a uniform shape and never renders
// `undefined`. Exported for a dedicated transform unit test.
export const transformCannedResponse = (
  wire: CannedResponseWire
): CannedResponse => ({
  responseType: wire.response_type,
  body: wire.body ?? '',
  generated: Boolean(wire.generated),
  generatedAt: wire.generated_at ?? null,
})

// Fail-soft list mapper (mirrors transformMakeGoodOffers): a null / malformed /
// non-array body coerces to [] rather than throwing (a throw would become a
// rejected query, not empty data), so a 404 while the store is unshipped degrades
// to an empty tab. Exported for a dedicated transform unit test.
export const transformCannedResponses = (
  response: CannedResponsesResponseWire
): CannedResponse[] =>
  Array.isArray(response?.canned_responses)
    ? response.canned_responses.map(transformCannedResponse)
    : []

// E3 preparer->admin activation-request notify (AI-923): maps one wire entry
// (raw-int admin_merchant_user_id) to the camelCase domain shape (stringified
// id, matching every other FE-facing merchant_user id). Exported for a
// dedicated transform unit test.
export const transformPreparerAdmin = (
  wire: PreparerAdminWire
): PreparerAdmin => ({
  id: String(wire.admin_merchant_user_id),
  name: wire.name,
  email: wire.email,
  notifiedAt: wire.notified_at ?? null,
})

// GET recovery/activation_requests returns a bare array (no envelope). Fail-soft
// like transformCannedResponses: a null/malformed body coerces to [].
export const transformPreparerAdmins = (
  response: PreparerAdminWire[]
): PreparerAdmin[] =>
  Array.isArray(response) ? response.map(transformPreparerAdmin) : []

// Map a wire location-config row (raw-int `location_id`, `name`, `enabled`) to
// the camelCase row the picker reads (`id` number preserved). Shared by the GET
// list and the PUT response (#619 returns the refreshed flat list). `name`
// coalesces a null/missing wire value to '' (AI-1173) — this boundary has no
// buildTranslate access, so the translated "Unnamed location" fallback for a
// blank name is resolved downstream, in useActiveLocationsController.
const normalizeLocationConfigRows = (
  response: PaginatedResponseWire<LocationConfigRowWire>
): PaginatedResponse<LocationConfigRow> => ({
  meta: normalizePaginationMeta(response.meta),
  data: response.data.map(row => ({
    id: row.location_id,
    name: row.name ?? '',
    enabled: row.enabled,
  })),
})

// `getAvailableRewards` pages through the server-paginated available_rewards
// endpoint. Matches the API's default page size; MAX_PAGES is a safety bound on
// the fetch-until-drained loop (50 pages * 50 = 2500 published templates).
const AVAILABLE_REWARDS_PER_PAGE = 50
const AVAILABLE_REWARDS_MAX_PAGES = 50

// `getLocationConfigs` drains EVERY page of recovery/location_configs and merges
// the rows, so ActiveLocationsPane's mode-derivation / enabledNow / isDirty see
// all of a merchant's locations — a single-page fetch silently under-saves a
// >100-location merchant (rows 101+ never make it into the PUT payload).
// per_page is bounded 1..100 server-side; MAX_PAGES caps the loop (50 * 100 =
// 5000 locations) the same way AVAILABLE_REWARDS_MAX_PAGES bounds the rewards drain.
const LOCATION_CONFIGS_PER_PAGE = 100
const LOCATION_CONFIGS_MAX_PAGES = 50

// Narrow the wire's free-form `resolved_by.kind` string to the FE union at the
// transform boundary; an unrecognized/absent kind degrades to null (the UI then
// takes the operator-name / em-dash branch). Keeps arbitrary backend strings
// from leaking into the typed domain.
const toResolvedByKind = (
  kind: string | null | undefined
): ResolvedByKind | null => (kind === 'ai' || kind === 'operator' ? kind : null)

// Maps the ConversationDetailEntity wire shape (snake_case, #455/#449
// enrichment) to the camelCase domain `Conversation`. Exported for a
// dedicated unit test — the slice's transformResponse just delegates here.
// Nil-safe throughout: PII-gated fields arrive present-but-null; `order`/
// `resolution`/`escalation` arrive as whole-object null; and the nested
// objects (`loyalty_tier`, `resolved_by`, `recent_activities`, `items`) are
// coalesced defensively so a legacy/edge row that omits one can't crash the
// whole query (the entity itself notes pre-PR-A rows with a NULL resolved_at).
export const transformConversation = (
  response: ConversationWire
): Conversation => ({
  id: response.id,
  status: response.status,
  caseSummary: response.case_summary,
  messages: response.messages,
  customer: {
    name: response.customer.name,
    firstName: response.customer.first_name,
    lastName: response.customer.last_name,
    email: response.customer.email,
    phone: response.customer.phone,
    uid: response.customer.uid ?? null,
    loyaltyTier: {
      token: response.customer.loyalty_tier?.token ?? null,
      name: response.customer.loyalty_tier?.name ?? null,
    },
    totalVisits: response.customer.total_visits,
    lifetimeSpendCents: response.customer.lifetime_spend_cents,
    priorResolutionCount: response.customer.prior_resolution_count,
  },
  recentActivities: (response.recent_activities ?? []).map(activity => ({
    id: activity.id,
    date: activity.date,
    resolutionType: activity.resolution_type,
    orderNumber: activity.order_number,
    // Real per-row enrichment (#545/#565); narrow the kind at the boundary and
    // coalesce the nested object so a legacy row missing it can't crash.
    resolvedBy: {
      kind: toResolvedByKind(activity.resolved_by?.kind),
      name: activity.resolved_by?.name ?? null,
    },
    rewardName: activity.reward_name ?? null,
  })),
  timeline: response.timeline,
  order: response.order
    ? {
        orderId: response.order.order_id,
        orderNumber: response.order.order_number,
        locationName: response.order.location_name,
        orderDate: response.order.order_date,
        orderTotalCents: response.order.order_total_cents,
        subtotalCents: response.order.subtotal_cents ?? null,
        taxCents: response.order.tax_cents,
        promo: response.order.promo,
        discounts: (response.order.discounts ?? []).map(discount => ({
          label: discount.label,
          amountCents: discount.amount_cents,
        })),
        deliveryMethod: response.order.delivery_method,
        customerFirstName: response.order.customer_first_name ?? null,
        items: (response.order.items ?? []).map(item => ({
          name: item.name,
          quantity: item.qty,
          priceCents: item.price_cents,
          modifier: item.modifier ?? null,
        })),
      }
    : null,
  resolution: response.resolution
    ? {
        type: response.resolution.type,
        status: response.resolution.status,
        costCents: response.resolution.cost_cents,
        rewardName: response.resolution.reward_name,
        resolvedAt: response.resolution.resolved_at,
        resolvedBy: {
          kind: toResolvedByKind(response.resolution.resolved_by?.kind),
          name: response.resolution.resolved_by?.name ?? null,
        },
      }
    : null,
  escalation: response.escalation
    ? {
        escalationType: response.escalation.escalation_type,
        reason: response.escalation.reason,
        status: response.escalation.status,
      }
    : null,
  // Coalesce to 0 for legacy/edge rows and until the backend ships the field —
  // the Restrict modal's escalations panel stays hidden while this is 0.
  otherOpenEscalationsCount: response.other_open_escalations_count ?? 0,
  // Coalesce to [] for legacy/edge rows and until the backend ships the field.
  otherOpenEscalationIds: response.other_open_escalation_ids ?? [],
  // Coalesce to false until the backend ships the field — the Restrict button
  // stays enabled (today's behavior) rather than disabling on a guess.
  customerRestricted: response.customer_restricted ?? false,
})

// Maps the escalation LINKAGE wire body (snake_case) to the camelCase domain
// shape. Exported for a dedicated transform unit test — the slice's
// transformResponse delegates here (mirrors transformRestrictedCustomer).
export const transformEscalation = (wire: EscalationWire): Escalation => ({
  id: wire.id,
  status: wire.status,
  escalationType: wire.escalation_type,
  conversationId: wire.conversation_id,
})

/**
 * Shared RTK mutation cache key for the canned-response rebuild that a
 * brand-voice save triggers (see updateRecoveryConfig's onQueryStarted).
 *
 * The rebuild is dispatched from the api layer, not from a component, so no
 * `useRegenerateCannedResponsesMutation()` tuple owns its pending state. Passing
 * this key to that hook subscribes a component to the SAME mutation state
 * instead of creating a second one — which is how the sandbox's greeting bubble
 * can render "adapting" for a rebuild it never started.
 *
 * A constant (not a literal at each site) because a typo would silently yield an
 * always-idle second cache entry, i.e. an indicator that never appears.
 */
export const BRAND_VOICE_REGEN_CACHE_KEY = 'brandVoiceCannedRegen'

// Fallback running mode for activateRecovery when a caller omits
// ActivateRecoveryBody's operating_mode — every caller today activates into
// full_auto, so this doubles as that default. Typed against the full
// OperatingMode union (not narrowed to a literal) because the audited endpoint
// itself accepts any of the three modes.
const ACTIVATE_RUNNING_MODE: OperatingMode = 'full_auto'

// The activation PUT answers 400 `{errors:{code:'live_updates_required',
// messages:[...]}}` while Live Updates is not active.
export const LIVE_UPDATES_REQUIRED_CODE = 'live_updates_required'

export const isLiveUpdatesRequired = (error: unknown): boolean =>
  getErrorCode(isPlainObject(error) ? error.data : undefined) ===
  LIVE_UPDATES_REQUIRED_CODE

export const recoveryAiApi = api.injectEndpoints({
  endpoints: builder => ({
    // Configuration
    getRecoveryConfig: builder.query<RecoveryConfig, void>({
      query: () => 'recovery/configuration',
      transformResponse: (response: {
        recovery_configuration: RecoveryConfig
      }) => response.recovery_configuration,
      providesTags: ['RecoveryConfig'],
    }),
    // Entitlement probe for the RecoveryAI nav item + the /recovery_ai route
    // (nexus #1577). UNGATED server-side — unlike every other endpoint in this
    // file, which requires merchant_settings — because a user WITHOUT that
    // permission must still be able to learn whether this merchant has
    // RecoveryAI set up: not set up ⇒ /recovery_ai is the marketing page, open
    // to any authenticated merchant user; set up ⇒ it is the live console,
    // gated on MerchantSettings as before.
    //
    // The body is a ROOTED Grape entity — `{"recovery_status": {configured,
    // active}}` — like every other recovery GET (only the mutations answer with
    // a bare hash), so it needs the same root-unwrapping transform
    // getRecoveryConfig uses. No case mapping beyond that: both keys are single
    // words, so the unwrapped object is already the shape components read.
    //
    // The unwrap is pinned against the ROOTED shape in recoveryAi.status.test.ts
    // at the msw boundary, deliberately: if the root is ever renamed server-side
    // this yields `undefined`, which reads as "not configured" and leaves the nav
    // item + route permanently UNGATED — a silent fail-OPEN, the dangerous
    // direction. That has to fail a test, not a code review.
    getRecoveryStatus: builder.query<RecoveryStatus, void>({
      query: () => 'recovery/status',
      transformResponse: (response: { recovery_status: RecoveryStatus }) =>
        response.recovery_status,
      providesTags: ['RecoveryStatus'],
    }),
    // Backs the Order feedback Overview page's "N of M locations on" badge.
    // Not deployed yet (thanx/nexus#1943, open) — callers must treat isError the
    // same as "coverage unknown" (see useOverviewSignals), never as zero.
    getLocationConfigsSummary: builder.query<LocationConfigsSummary, void>({
      query: () => 'recovery/location_configs/summary',
      // Throwing here surfaces as isError, which useOverviewSignals already
      // treats as "coverage unknown" — a malformed count must never silently
      // coerce into a confident 'full' or 'none' reading.
      transformResponse: (response: LocationConfigsSummaryWire) => {
        const summary = response?.location_configs_summary
        const enabledCount = summary?.enabled_count
        const totalCount = summary?.total_count

        if (
          typeof enabledCount !== 'number' ||
          !Number.isFinite(enabledCount) ||
          typeof totalCount !== 'number' ||
          !Number.isFinite(totalCount)
        ) {
          throw new Error('Invalid location configuration summary counts')
        }

        return { enabledCount, totalCount }
      },
      providesTags: ['RecoveryLocationConfigs'],
    }),
    // The public config PUT never accepts operating_mode: the backend permits
    // only 'feedback_only' | 'off' there and rejects 'full_auto' outright, so the
    // body type omits the field entirely — tsc catches an attempt to send it,
    // rather than a 400 from production. Every operating_mode write goes through
    // the audited activateRecovery mutation below instead.
    updateRecoveryConfig: builder.mutation<
      RecoveryConfig,
      Partial<Omit<RecoveryConfig, 'operating_mode'>>
    >({
      query: body => ({
        url: 'recovery/configuration',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: {
        recovery_configuration: RecoveryConfig
      }) => response.recovery_configuration,
      // Read-your-writes: write the PUT's own (authoritative, full) response into
      // the getRecoveryConfig cache the instant it resolves, so consumers — the
      // chat simulator's conversationContext and the wizard's Finalize gate — see
      // the just-saved brand voice / posture / notify flag immediately.
      //
      //   context: this only reaches consumers because the success invalidation
      //   below is withheld. RTK's queryStatePreSelector serves `lastResult.data`
      //   while a refetch is PENDING (isSuccess is false), and an invalidation
      //   dispatches that pending synchronously on fulfilment — before this patch
      //   lands a microtask later. So while the refetch runs, the store holds the
      //   patch and no hook can read it: a patch PLUS an unconditional invalidation
      //   is strictly worse than either alone. Measured, not reasoned — the
      //   HOOK-level case in recoveryAi.test.ts is the one that catches it; a
      //   `.select()` assertion on the store passes either way.
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          const { data: updated } = await queryFulfilled
          dispatch(
            recoveryAiApi.util.updateQueryData(
              'getRecoveryConfig',
              undefined,
              () => updated
            )
          )
          // Drive the canned-response rebuild from the ONE place every
          // brand-voice save passes through.
          //
          // Regeneration already had a trigger: nucleus's
          // `after_update_commit :enqueue_canned_regen` → the `looker` Sidekiq
          // queue. That is a fine BE guarantee and stays — it also covers writers
          // this file never sees (admin console, the AI-380 mimic write-back) — but
          // it is worthless as a UX signal: the merchant gets an instant "Saved",
          // the copy changes ~10s later at an unobservable moment, and wherever the
          // queue has no consumer it never changes at all (the AI-932 failure mode,
          // and the entire local-dev experience). So ALSO drive the inline
          // POST /regenerate, which runs synchronously on the BE (the async
          // job is unconditional too — nexus#1849), and self-invalidates on
          // success.
          //
          //   context: fired HERE rather than at the call sites on purpose. There
          //   are two independent save seams — RecoveryConfigDraftProvider#commit
          //   (wizard + sandbox + agent-tuning modals) and Settings/useSaveAndTest
          //   (the settings panes) — and a third would be easy to add without
          //   noticing this. Hanging it off the mutation mirrors what nucleus does
          //   with the model callback: one hook, every writer, no coordination.
          //   Cost of the belt-and-braces overlap is one extra LLM call per voice
          //   save; Store is an upsert, so the two racing writes differ only in
          //   model nondeterminism, never in integrity.
          //
          // Gated on the KEY being present, not on a value diff: `staged` /
          // buildPayload only carry keys the merchant actually touched (and the
          // providers already dirty-check before PUTting), so key-presence is the
          // narrowest signal available here. Unrelated writes — Finalize,
          // posture, rewards — never carry it and never pay for a rebuild.
          if (body.brand_voice_text !== undefined) {
            // fixedCacheKey publishes this mutation's pending/error state to any
            // component that asks for the same key, which is how the sandbox's
            // greeting bubble shows "adapting" for a rebuild it did not start.
            dispatch(
              recoveryAiApi.endpoints.regenerateCannedResponses.initiate(
                undefined,
                { fixedCacheKey: BRAND_VOICE_REGEN_CACHE_KEY }
              )
            )
          }
        } catch {
          // Fail soft: leave the cache; the invalidation refetch still corrects it.
          // Also the reason the regen dispatch sits INSIDE the try, after the
          // await: a rejected PUT never persisted the voice, so rebuilding would
          // generate copy for a brand voice the merchant does not have.
        }
      },
      // Invalidate on FAILURE ONLY (mirrors setEscalationContacts). On success the
      // patch above already holds the PUT's own authoritative response, and
      // getRecoveryConfig is the only provider of this tag — so a refetch buys
      // nothing and costs a window in which a lagging read replica serves the
      // PRE-write config and OVERWRITES the patch. That window is not cosmetic on
      // the notify leg: useActivationCompleteness short-circuits open on
      // escalation_routing_notify_location_managers, so a resurrected `true`
      // ENABLES Finalize while persisted truth is notify OFF with no recipient.
      // On failure the cache was deliberately left alone, so the refetch is the
      // only thing that reconciles it. (activateRecovery keeps its unconditional
      // invalidation — it has no read-your-writes patch to preserve.)
      //
      // 'RecoveryStatus' is invalidated UNCONDITIONALLY, and that is not a
      // contradiction of the paragraph above: the read-your-writes argument is
      // about the getRecoveryConfig cache entry, which this mutation patches by
      // hand. getRecoveryStatus has no such patch (the PUT's response body
      // doesn't carry the probe's shape), so a refetch is the ONLY thing that
      // can correct it. It has to happen: the wizard's Finalize flips
      // onboarding_completed through THIS mutation, and a stale
      // `configured: false` would leave the nav item and the route ungated for
      // a merchant who just finished setup — i.e. the console served to a user
      // with no merchant_settings permission. The probe is a two-boolean body,
      // so re-fetching it after any config write is cheap insurance.
      //
      // 'RecoveryCannedResponses' is invalidated UNCONDITIONALLY for the same
      // reason as 'RecoveryStatus', and it is NOT in tension with the withheld
      // 'RecoveryConfig' success invalidation: the race that paragraph describes
      // is between a refetch and this mutation's hand-written patch, and there is
      // no patch for the canned-responses cache — a refetch is the only thing that
      // can correct it. Without this the sandbox served the pre-save greeting for
      // RTK's full 60s keepUnusedDataFor (no global override; useSimulatorData
      // subscribes with no options, unlike CannedResponsesPane's
      // refetchOnMountOrArgChange), which is the visible half of "saving a new
      // brand voice doesn't change the opening line".
      invalidatesTags: (_result, error) =>
        error
          ? ['RecoveryConfig', 'RecoveryStatus', 'RecoveryCannedResponses']
          : ['RecoveryStatus', 'RecoveryCannedResponses'],
    }),
    // Activation. The audited PUT recovery/configuration/activation — the ONLY
    // caller of Recovery::UpdateActivation, which fires
    // MakeGoodRewards::Materialize/Provision on the false→true enable transition
    // (nexus #1164/#1165). Reached only from ActivationPricingModal's "Agree &
    // activate" CTA; Finalize never activates (see useWizardSubmit).
    // `billingMethod` / available_credit / activated_at / authorized_by are not on
    // the wire; `billingMethod` rides the typed arg for the future billing contract.
    activateRecovery: builder.mutation<RecoveryConfig, ActivateRecoveryBody>({
      query: ({ operating_mode }) => ({
        url: 'recovery/configuration/activation',
        method: 'PUT',
        body: { operating_mode: operating_mode ?? ACTIVATE_RUNNING_MODE },
      }),
      transformResponse: (response: {
        recovery_configuration: RecoveryConfig
      }) => response.recovery_configuration,
      // 'RecoveryActivation' so the success page's getActivation refetches the
      // provenance the PUT itself doesn't return (ConfigurationEntity excludes it).
      invalidatesTags: [
        'RecoveryConfig',
        'RecoveryStatus',
        'RecoveryActivation',
      ],
    }),
    // GET recovery/configuration/activation (AI-1008, nexus #1210) — the
    // authoritative activation provenance. Owns activatedAt/authorizedBy* so a
    // refresh/direct visit reads the real activation event instead of the
    // client clock/current viewer the pricing modal used to hand off. Null-guard
    // the whole authorized_by object: a never-enabled merchant gets an unsaved
    // ActivationEvent (all-null keys), never a missing key.
    getActivation: builder.query<
      {
        activatedAt: string | null
        authorizedByName: string | null
        authorizedByEmail: string | null
      },
      void
    >({
      query: () => 'recovery/configuration/activation',
      transformResponse: (response: ActivationSummaryResponse) => ({
        activatedAt: response.activation.activated_at,
        // .trim() || null: nexus builds the name as "#{first} #{last}".strip, which
        // is "" (not null) for a user with both halves blank — normalize blank to
        // null here so the string | null contract actually holds.
        authorizedByName:
          response.activation.authorized_by?.name?.trim() || null,
        authorizedByEmail: response.activation.authorized_by?.email ?? null,
      }),
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (err) {
          // A rejected GET (RBAC/500) renders identically to "never activated" —
          // report it so a real failure doesn't hide silently behind the em-dash.
          errorHandler.capture(
            'Recovery getActivation failed; activation provenance rendered as unknown',
            { error: err }
          )
        }
      },
      providesTags: ['RecoveryActivation'],
    }),
    // E3 preparer->admin handoff (AI-923): the RBAC-eligible admins a preparer can
    // notify, each carrying an authoritative notifiedAt. Replaces the old
    // client-side merchant_users-filtered sourcing in usePreparerAdmins — this
    // endpoint is a flagged, 404-when-off dark launch, so the hook still needs its
    // own "unavailable" read on a 404 (the flag being off is not an error).
    getActivationAdmins: builder.query<PreparerAdmin[], void>({
      query: () => 'recovery/activation_requests',
      transformResponse: transformPreparerAdmins,
      providesTags: ['RecoveryActivationAdmins'],
    }),
    // Idempotent notify/resend — see Recovery::AdminActivationRequests::Notify:
    // re-calling this for the same admin upserts the same row and refreshes
    // notified_at, so there is no separate resend mutation. `void` — the response
    // is unused (204); a successful call only needs the list re-fetched so the
    // notified row flips to "Notified …" (mirrors sendConversationMessage).
    notifyActivationAdmin: builder.mutation<void, NotifyActivationAdminParams>({
      query: ({ adminMerchantUserId, message }) => ({
        url: 'recovery/activation_requests/notify',
        method: 'POST',
        body: {
          admin_merchant_user_id: Number(adminMerchantUserId),
          message,
        },
      }),
      invalidatesTags: ['RecoveryActivationAdmins'],
    }),
    // Brand-voice "Generate from documents" derive (AI-299). The file is PUT
    // direct-to-S3 via the presigned-URL flow; this tiny JSON POST hands the
    // server the returned S3 key, and the server synchronously asks the brain to
    // DERIVE a brand voice from it and RETURNS the text (200) WITHOUT persisting
    // — the merchant reviews it in the textarea and saves via the config PUT.
    // No invalidatesTags: nothing server-side changed here (return-not-persist).
    // A pre-W7 nexus answers 202 + ConfigurationEntity, so `recovery_brand_voice`
    // is absent → brand_voice_text falls to null → the hook treats it as
    // "couldn't derive". Server-gated by recovery-ai.
    uploadBrandVoiceDocument: builder.mutation<
      BrandVoiceDerivation,
      UploadBrandVoiceDocumentBody
    >({
      query: body => ({
        url: 'recovery/brand_voice',
        method: 'POST',
        body,
      }),
      transformResponse: (response: {
        recovery_brand_voice?: { brand_voice_text?: string | null }
      }) => ({
        brand_voice_text:
          response?.recovery_brand_voice?.brand_voice_text ?? null,
      }),
    }),
    getAvailableRewards: builder.query<AvailableReward[], void>({
      // `recovery/available_rewards` is server-paginated (the API caps each
      // page at AVAILABLE_REWARDS_PER_PAGE) and returns a bare array with NO
      // pagination meta, so we can't read a total to know when to stop. Page
      // through until a page comes back smaller than the page size (the last
      // page), concatenating results — otherwise a merchant with more than one
      // page of published reward templates would be silently truncated.
      // AVAILABLE_REWARDS_MAX_PAGES bounds the loop in case the API ever
      // returns a full page indefinitely.
      async queryFn(_arg, _api, _extraOptions, fetchWithBQ) {
        const rewards: AvailableReward[] = []
        // Set only if the loop runs to the cap on a still-full final page — i.e.
        // the short-page break never fired, so the list may be incomplete.
        // Best-effort / over-reports at the exact boundary: with no pagination
        // meta we can't distinguish a truly-capped list from one whose total is an
        // exact multiple of the page size (e.g. a full 2,500 templates) — the
        // final page is full, nothing was dropped, yet this still trips. Read the
        // resulting Sentry signal as "likely truncated", not proof of loss.
        let truncated = false

        for (let page = 1; page <= AVAILABLE_REWARDS_MAX_PAGES; page++) {
          const result = await fetchWithBQ({
            url: 'recovery/available_rewards',
            params: { page, per_page: AVAILABLE_REWARDS_PER_PAGE },
          })

          if (result.error) {
            return { error: result.error }
          }

          const { available_rewards } = result.data as {
            available_rewards: AvailableReward[]
          }
          rewards.push(...available_rewards)

          if (available_rewards.length < AVAILABLE_REWARDS_PER_PAGE) {
            break
          }
          if (page === AVAILABLE_REWARDS_MAX_PAGES) {
            truncated = true
          }
        }

        // AI-92: reaching the cap with a full final page used to be a SILENT
        // fulfilled result — the exact silent-truncation the fetch-until-drained
        // loop was meant to prevent, relocated to the bound. Make it observable
        // (Sentry in prod) while still returning the partial data, since the
        // endpoint ships no pagination meta to detect incompleteness otherwise.
        if (truncated) {
          errorHandler.capture(
            'Recovery available_rewards paging hit the AVAILABLE_REWARDS_MAX_PAGES cap; reward list may be truncated',
            {
              pages: AVAILABLE_REWARDS_MAX_PAGES,
              count: rewards.length,
            }
          )
        }

        return { data: rewards }
      },
      providesTags: ['RecoveryAvailableRewards'],
    }),

    // Activity
    getRecoveryActivities: builder.query<
      PaginatedResponse<Activity>,
      ActivityQueryParams
    >({
      query: params => ({
        url: 'recovery/activities',
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<ActivityResponse>
      ): PaginatedResponse<Activity> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(a => ({
          id: a.id,
          orderNumber: a.order_number,
          customerName: a.customer_name,
          storeName: a.store_name,
          date: a.date,
          status: a.status,
          conversationId: a.conversation_id,
          resolvedBy: a.resolved_by,
        })),
      }),
      providesTags: ['RecoveryActivities'],
    }),
    getActivityMetrics: builder.query<ActivityMetrics, void>({
      query: () => 'recovery/activities/metrics',
      transformResponse: (
        response: ActivityMetricsResponse
      ): ActivityMetrics => ({
        actionRequiredCount: response.action_required_count,
        totalLocationsCount: response.total_locations_count,
        activeLocationsCount: response.active_locations_count,
        guestFacingTotalLocationsCount:
          response.guest_facing_total_locations_count,
        guestFacingActiveLocationsCount:
          response.guest_facing_active_locations_count,
        // AI-901 pilot-vs-staff performance figures (number|null, always sent
        // once the backend ships AI-901; see context/ai-901-metrics-contract.md).
        pilotLocationsCount: response.pilot_locations_count,
        pilotResponseRate: response.pilot_response_rate,
        pilotResponseTimeMinutes: response.pilot_response_time_minutes,
        pilotReturnRate30d: response.pilot_return_rate_30d,
        staffReturnRate30d: response.staff_return_rate_30d,
        isOutperformingStaff: response.is_outperforming_staff,
      }),
      providesTags: ['RecoveryMetrics'],
    }),
    // Per-location enablement list (BE-7/AI-26, #619). Paginated/searchable; the
    // wire row's raw-integer `location_id` is mapped to a camelCase `id`
    // (number preserved) so no snake_case row shape leaks past the slice.
    getLocationConfigs: builder.query<
      PaginatedResponse<LocationConfigRow>,
      void
    >({
      // Page through until drained, concatenating rows. The endpoint returns
      // pagination meta, so we stop once we've fetched `total_pages` (or hit
      // the LOCATION_CONFIGS_MAX_PAGES safety bound). Mirrors
      // getAvailableRewards' fetch-until-drained loop; a single-page fetch
      // would truncate the list and silently under-save a >100-location merchant.
      async queryFn(_arg, _api, _extraOptions, fetchWithBQ) {
        const rows: LocationConfigRow[] = []
        // Overwritten by page 1's meta on the first iteration; the placeholder
        // only matters in the impossible "loop never ran" case (page starts at 1).
        let meta: PaginationMeta = {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          perPage: LOCATION_CONFIGS_PER_PAGE,
        }

        for (let page = 1; page <= LOCATION_CONFIGS_MAX_PAGES; page++) {
          const result = await fetchWithBQ({
            url: 'recovery/location_configs',
            params: { page, per_page: LOCATION_CONFIGS_PER_PAGE },
          })

          if (result.error) {
            return { error: result.error }
          }

          const normalized = normalizeLocationConfigRows(
            result.data as PaginatedResponseWire<LocationConfigRowWire>
          )
          rows.push(...normalized.data)
          meta = normalized.meta

          if (page >= normalized.meta.totalPages) {
            break
          }
        }

        // The final page's meta carries the authoritative totalCount/totalPages
        // for the full set; data holds every drained row.
        return { data: { meta, data: rows } }
      },
      providesTags: ['RecoveryLocationConfigs'],
    }),
    // Bulk per-location enable/disable (BE-7/AI-26, #619) — the ONLY write path
    // for `enabled` (the config PUT does NOT accept location_configs), so this
    // does NOT reuse useSaveAndTest. Returns the refreshed flat list; invalidates
    // the list + the config + the metrics (N/M) so the header/count refetch.
    updateLocationConfigs: builder.mutation<
      PaginatedResponse<LocationConfigRow>,
      UpdateLocationConfigsBody
    >({
      query: body => ({
        url: 'recovery/location_configs',
        method: 'PUT',
        body,
      }),
      transformResponse: normalizeLocationConfigRows,
      invalidatesTags: [
        'RecoveryLocationConfigs',
        'RecoveryConfig',
        'RecoveryMetrics',
      ],
    }),

    // Conversations
    getConversation: builder.query<Conversation, string>({
      query: id => `recovery/conversations/${id}`,
      transformResponse: transformConversation,
      providesTags: (_result, _error, id) => [
        { type: 'RecoveryConversation', id },
      ],
    }),
    resolveCase: builder.mutation<Conversation, ResolveCaseParams>({
      query: ({ id, ...body }) => ({
        url: `recovery/conversations/${id}/resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'RecoveryConversation', id },
        'RecoveryActivities',
        'RecoveryMetrics',
      ],
    }),
    // Send-only: emails the customer a subject + message WITHOUT resolving the case
    // (merchant-api POST recovery/conversations/:id/messages, unflagged). `void` —
    // the response body is unused; a successful send only needs the conversation's
    // transcript tag invalidated (it appended a new turn), NOT RecoveryActivities /
    // RecoveryMetrics — unlike resolveCase, nothing about status/resolution changes.
    sendConversationMessage: builder.mutation<
      void,
      SendConversationMessageParams
    >({
      query: ({ id, ...body }) => ({
        url: `recovery/conversations/${id}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'RecoveryConversation', id },
      ],
    }),
    // Restrict the customer behind a case. A single POST to
    // recovery/restricted_customers both adds the customer to the restricted
    // list AND auto-resolves the source case (customer_restricted →
    // manually_resolved) atomically on the backend (#569) — so the FE makes NO
    // separate resolve call. The response is unused (we refetch via tags), so
    // the result is `void`, mirroring whitelistCustomer.
    restrictCustomer: builder.mutation<void, RestrictCustomerParams>({
      query: ({ conversation_id, close_escalation_ids }) => ({
        url: 'recovery/restricted_customers',
        method: 'POST',
        // Only send close_escalation_ids when the caller sets it (customer has
        // other open escalations) — otherwise omit it and let the backend default.
        body: {
          conversation_id,
          ...(close_escalation_ids === undefined
            ? {}
            : { close_escalation_ids }),
        },
      }),
      // Invalidate on SUCCESS, and on the specific 422 "already restricted"
      // failure — but no other failure. CaseDetail's loading gate now keeps a
      // background refetch from tearing down the open confirm modal ("modal
      // bounce"), so a 422 reconciling the cache is safe: it's the one failure
      // where the FE's belief about this customer (customerRestricted) is
      // actually stale, and only a refetch picks up the corrected value so the
      // Restrict button can self-disable. A non-422 failure (network, 5xx) is
      // NOT reconciled — that's the transient-blip case, and forcing a refetch
      // buys nothing but a wasted request.
      // On success/422, invalidate the same tags as resolveCase so the case
      // refetches closed (or restricted) and drops out of the action-required
      // queue; plus AI-303 (FR-41) the suspected-gaming queue and the
      // restricted list (both read views of the customer whose state changed).
      invalidatesTags: (_result, error, { conversation_id }) => {
        const isAlreadyRestricted =
          isFetchBaseQueryError(error) && error.status === 422
        if (error && !isAlreadyRestricted) return []
        return [
          { type: 'RecoveryConversation', id: conversation_id },
          'RecoveryActivities',
          'RecoveryMetrics',
          'RecoveryGamingReview',
          'RecoveryRestrictedCustomers',
          // Inbox: restrict is customer-scoped (every case of this guest now
          // reads `restricted`) and can close other escalations, and the arg
          // has a conversation id, not the rating hashid, so invalidate every
          // FeedbackInboxItem.
          'FeedbackInbox',
          'FeedbackInboxCounts',
          'FeedbackInboxItem',
        ]
      },
    }),

    // Escalations
    // Linkage detail (GET recovery/escalations/:id) — powers the
    // /escalations/:id → case-detail redirect. Scoped id tag.
    getEscalation: builder.query<Escalation, string>({
      query: id => `recovery/escalations/${id}`,
      transformResponse: transformEscalation,
      providesTags: (_result, _error, id) => [
        { type: 'RecoveryEscalation', id },
      ],
    }),
    // acknowledge / resolve are NON-money AASM transitions returning the linkage
    // body. Invalidate the scoped detail id + the FLAT queue tag + the source
    // conversation (keyed off the returned conversationId) so the case-detail
    // escalation block re-gates its buttons after the action.
    acknowledgeEscalation: builder.mutation<Escalation, { id: string }>({
      query: ({ id }) => ({
        url: `recovery/escalations/${id}/acknowledge`,
        method: 'POST',
      }),
      transformResponse: transformEscalation,
      invalidatesTags: (result, _error, { id }) => [
        { type: 'RecoveryEscalation', id },
        'RecoveryEscalation',
        'RecoveryActivities',
        ...(result
          ? [
              {
                type: 'RecoveryConversation' as const,
                id: result.conversationId,
              },
            ]
          : []),
      ],
    }),
    resolveEscalation: builder.mutation<Escalation, ResolveEscalationParams>({
      query: ({ id, ...body }) => ({
        url: `recovery/escalations/${id}/resolve`,
        method: 'POST',
        body,
      }),
      transformResponse: transformEscalation,
      invalidatesTags: (result, _error, { id }) => [
        { type: 'RecoveryEscalation', id },
        'RecoveryEscalation',
        'RecoveryActivities',
        'RecoveryMetrics',
        ...(result
          ? [
              {
                type: 'RecoveryConversation' as const,
                id: result.conversationId,
              },
            ]
          : []),
      ],
    }),

    // Flagged Customers
    getFlaggedCustomers: builder.query<
      PaginatedResponse<FlaggedCustomer>,
      { page: number; per_page: number }
    >({
      query: params => ({
        url: 'recovery/flagged_customers',
        params,
      }),
      // FlaggedCustomer's own fields stay snake_case for now because they
      // ship from a legacy endpoint that hasn't been re-shaped. Pagination
      // meta is the one part the merchant-ui convention says we must
      // normalize so components don't reach into snake_case keys.
      transformResponse: (
        response: PaginatedResponseWire<FlaggedCustomer>
      ): PaginatedResponse<FlaggedCustomer> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data,
      }),
      providesTags: ['RecoveryFlagged'],
    }),
    whitelistCustomer: builder.mutation<void, WhitelistParams>({
      query: ({ user_id, ...body }) => ({
        url: `recovery/flagged_customers/${user_id}/whitelist`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RecoveryFlagged'],
    }),
    removeWhitelist: builder.mutation<void, { user_id: string }>({
      query: ({ user_id }) => ({
        url: `recovery/flagged_customers/${user_id}/whitelist`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RecoveryFlagged'],
    }),

    // Restricted Customers (AI-41) — the NEW restricted-customer resource
    // (#569 list/DELETE + AI-178 #617 aggregates). NOT the legacy flagged/whitelist
    // endpoints above (exempt-from-AI, the inverse semantics).
    getRestrictedCustomers: builder.query<
      PaginatedResponse<RestrictedCustomer>,
      RestrictedCustomerQueryParams
    >({
      query: params => ({
        url: 'recovery/restricted_customers',
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<RestrictedCustomerWire>
      ): PaginatedResponse<RestrictedCustomer> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(transformRestrictedCustomer),
      }),
      providesTags: ['RecoveryRestrictedCustomers'],
    }),

    // Gaming review (AI-303, FR-41) — the read-only suspected-gaming queue. The
    // brain (AI-304) flags customers who crossed the merchant's auto_resolution
    // threshold; the merchant reviews and Restricts. CONDITIONAL:
    // recovery/gaming_review is a net-new nexus/brain deliverable (TBC-Darshan) —
    // until it ships the query returns error/empty and the pane renders its
    // empty/error state. Reuses the {data,meta} envelope + normalizePaginationMeta,
    // delegating each row to transformGamingReviewRow (mirrors getRestrictedCustomers).
    getGamingReviewCustomers: builder.query<
      PaginatedResponse<GamingReviewCustomer>,
      GamingReviewQueryParams
    >({
      query: params => ({
        url: 'recovery/gaming_review',
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<GamingReviewCustomerWire>
      ): PaginatedResponse<GamingReviewCustomer> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(transformGamingReviewRow),
      }),
      providesTags: ['RecoveryGamingReview'],
    }),
    // Lift a restriction. The arg is the raw-int row `id` (a string on the wire);
    // it is used verbatim as the url segment (the DELETE route coerces it back to
    // an int server-side — no hashid decode). #569 soft-lifts (active→lifted) and
    // returns 204; from the FE's view the verb is DELETE, the body is empty, and
    // the lifted row drops out of the active-only list on refetch via tag invalidation.
    removeRestriction: builder.mutation<void, string>({
      query: id => ({
        url: `recovery/restricted_customers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RecoveryRestrictedCustomers'],
    }),

    // Budget
    getRecoveryBudget: builder.query<BudgetSummary, { location_id?: string }>({
      query: params => ({
        url: 'recovery/budget',
        params,
      }),
      providesTags: ['RecoveryBudget'],
    }),

    // Sandbox prompt (dev-only baseline override editable from the sandbox UI)
    getSandboxPrompt: builder.query<SandboxPrompt, void>({
      query: () => 'recovery/sandbox_prompt',
      transformResponse: (response: {
        recovery_sandbox_prompt: SandboxPrompt
      }) => response.recovery_sandbox_prompt,
      providesTags: ['RecoverySandboxPrompt'],
    }),
    updateSandboxPrompt: builder.mutation<SandboxPrompt, string>({
      query: promptText => ({
        url: 'recovery/sandbox_prompt',
        method: 'PUT',
        body: { prompt_text: promptText },
      }),
      transformResponse: (response: {
        recovery_sandbox_prompt: SandboxPrompt
      }) => response.recovery_sandbox_prompt,
      invalidatesTags: ['RecoverySandboxPrompt'],
    }),

    // Canned responses (AI-249) — per-merchant store of the five brand-voiced
    // messages (greeting first). The GET always returns the full set with safe
    // fallbacks (per Recovery::CannedResponses::Resolve), so a not-yet-generated
    // merchant still gets 5 entries; `generated` distinguishes a stored
    // per-merchant row from a fallback.
    // Fail-soft: transformCannedResponses coerces a null/malformed body to []
    // (mirrors makeGoodLadder), so a 404 while the store is unshipped degrades to
    // an empty tab; the consumer keeps isLoading/isError out of any page gate.
    getCannedResponses: builder.query<CannedResponse[], void>({
      query: () => 'recovery/canned_responses',
      transformResponse: transformCannedResponses,
      providesTags: ['RecoveryCannedResponses'],
    }),

    // AI-932: force a rebuild from the current brand voice. Regeneration was
    // previously reachable ONLY as a side effect of a brand-voice save landing on
    // the async `looker` worker, so wherever that queue has no consumer the copy
    // could never be rebuilt and the failure was silent. This runs inline on the
    // BE, so it works regardless of queue state.
    // invalidatesTags (not a cache write) keeps the GET authoritative for what the
    // pane renders — the POST returns the same shape, but one source of truth for
    // `generated`/`generatedAt` avoids the two diverging.
    regenerateCannedResponses: builder.mutation<CannedResponse[], void>({
      query: () => ({
        url: 'recovery/canned_responses/regenerate',
        method: 'POST',
      }),
      transformResponse: transformCannedResponses,
      invalidatesTags: ['RecoveryCannedResponses'],
    }),
  }),
})

export const {
  useGetRecoveryConfigQuery,
  useGetRecoveryStatusQuery,
  useUpdateRecoveryConfigMutation,
  useActivateRecoveryMutation,
  useGetActivationQuery,
  useGetActivationAdminsQuery,
  useNotifyActivationAdminMutation,
  useUploadBrandVoiceDocumentMutation,
  useGetAvailableRewardsQuery,
  useGetRecoveryActivitiesQuery,
  useGetActivityMetricsQuery,
  useGetLocationConfigsQuery,
  useGetLocationConfigsSummaryQuery,
  useUpdateLocationConfigsMutation,
  useGetConversationQuery,
  useResolveCaseMutation,
  useSendConversationMessageMutation,
  useRestrictCustomerMutation,
  useGetEscalationQuery,
  useAcknowledgeEscalationMutation,
  useResolveEscalationMutation,
  useGetFlaggedCustomersQuery,
  useWhitelistCustomerMutation,
  useRemoveWhitelistMutation,
  useGetRestrictedCustomersQuery,
  useRemoveRestrictionMutation,
  useGetGamingReviewCustomersQuery,
  useGetCannedResponsesQuery,
  useRegenerateCannedResponsesMutation,
  useGetRecoveryBudgetQuery,
  useGetSandboxPromptQuery,
  useUpdateSandboxPromptMutation,
} = recoveryAiApi
